import { Plus, X } from "lucide-react";
import { useId, useState } from "react";
import { Controller, type FieldValues } from "react-hook-form";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

import { COUNTRY_OPTIONS } from "./countries";
import { FieldShell } from "./FieldShell";
import { useFieldControl, type BaseFieldProps } from "./fieldTypes";

type Props<T extends FieldValues> = BaseFieldProps<T> & {
  /** Shown on the first chip, e.g. "Primary". */
  firstLabel?: string;
  addLabel?: string;
};

/** Several countries as removable chips (a `string[]` field); order is kept. */
export function CountryMultiField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  firstLabel,
  addLabel = "Add Country"
}: Props<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  const [open, setOpen] = useState(false);

  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => {
        const values: string[] = Array.isArray(field.value) ? field.value : [];
        const remaining = COUNTRY_OPTIONS.filter((c) => !values.includes(c));
        return (
          <FieldShell
            id={id}
            label={label}
            required={required}
            description={description}
            error={fieldState.error}
            disabled={disabled}
            className={className}
          >
            <div className="flex min-h-8 flex-wrap items-center gap-1.5">
              {values.map((country, i) => (
                <Badge
                  key={country}
                  variant="outline"
                  className="h-6 gap-1 pr-1 font-normal"
                >
                  {country}
                  {i === 0 && firstLabel && values.length > 1 ? (
                    <span className="text-xs text-muted-foreground">
                      {firstLabel}
                    </span>
                  ) : null}
                  {!disabled ? (
                    <button
                      type="button"
                      aria-label={`Remove ${country}`}
                      className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={() =>
                        field.onChange(values.filter((v) => v !== country))
                      }
                    >
                      <X className="size-3" />
                    </button>
                  ) : null}
                </Badge>
              ))}
              <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    id={id}
                    ref={field.ref}
                    type="button"
                    variant="secondary"
                    size="xs"
                    disabled={disabled}
                    onBlur={field.onBlur}
                  >
                    <Plus />
                    {values.length ? addLabel : "Select Country"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-0">
                  <Command>
                    <CommandInput placeholder="Search countries…" />
                    <CommandList>
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup>
                        {remaining.map((country) => (
                          <CommandItem
                            key={country}
                            value={country}
                            onSelect={() => {
                              field.onChange([...values, country]);
                              setOpen(false);
                            }}
                          >
                            {country}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FieldShell>
        );
      }}
    />
  );
}
